/**
 * The SINGLE web-side source of truth for the three-tier RBAC model.
 *
 * THREE TIERS (pinned contract — must match the API `getUserTier` resolver):
 *   - viewer   → read only (list/get); sees no action buttons
 *   - operator → start / stop / restart / deploy / rollback + devops write
 *                actions (queue/cancel build, update work item)
 *   - admin    → everything operator can do, PLUS exec into a container and
 *                host / connection / secret configuration
 *
 * SOURCE OF TRUTH: the API computes an EFFECTIVE `tier` for the authenticated
 * user (via its shared `getUserTier(user)` resolver, reading the real role
 * names — admin / controller / project_manager / ap_clerk / ar_clerk /
 * time_keeper / viewer — and the `adminUser` flag) and denormalises it onto the
 * authenticated user payload. The web NEVER re-derives that role→tier mapping;
 * it reads `currentUser.tier` directly so the two sides can never diverge.
 *
 * FALLBACK: if the API has not yet surfaced `tier` (e.g. an older token, or the
 * JWT/tier rollout has not reached this environment), we degrade to the only
 * reliable legacy signal — `adminUser === true` ⇒ 'admin', otherwise 'viewer'
 * (the safe, read-only floor). We deliberately do NOT guess 'operator' from any
 * legacy `role` string, because the legacy `role` carries the raw role name
 * (e.g. 'controller'), not a tier, and mapping it here would duplicate the API
 * logic this module exists to centralise.
 *
 * SECURITY: a green light here is never sufficient. These helpers only govern
 * whether the UI renders an action; the API re-checks the effective tier on
 * every mutating call and every stream/exec subscribe regardless of what the UI
 * shows. The Portainer admin key is root-equivalent and exec is admin-only.
 */
import type RootStore from "stores/RootStore";

/** The three effective tiers, lowest → highest privilege. */
export type UserTier = "viewer" | "operator" | "admin";

/**
 * Shape of the authenticated user the web reads for tier resolution. The library
 * `AuthStore.currentUser` is loosely typed, so this is the minimal contract the
 * tier helpers depend on. `tier` is the API-computed effective tier; `adminUser`
 * is the legacy fallback flag.
 */
export interface TierUserLike {
  /** API-computed effective tier. Authoritative when present. */
  tier?: UserTier | string;
  /** Legacy admin flag — the only reliable fallback signal. */
  adminUser?: boolean;
  /** Raw role name (NOT a tier) — present for display only; never mapped here. */
  role?: string;
}

const VALID_TIERS: ReadonlySet<string> = new Set<UserTier>([
  "viewer",
  "operator",
  "admin",
]);

function readCurrentUser(rootStore: RootStore): TierUserLike | undefined {
  return (
    rootStore as unknown as {
      authStore?: { currentUser?: TierUserLike };
    }
  ).authStore?.currentUser;
}

/**
 * Resolve the effective tier for a raw user payload.
 *
 * Prefers the API-computed `currentUser.tier`. Falls back to
 * `adminUser ? 'admin' : 'viewer'`. Returns `null` when there is no user at all
 * (not signed in) so callers can distinguish "no user" from "viewer".
 */
export function resolveTier(user: TierUserLike | undefined | null): UserTier | null {
  if (!user) return null;
  if (typeof user.tier === "string" && VALID_TIERS.has(user.tier)) {
    return user.tier as UserTier;
  }
  // Fallback: only the legacy admin flag is a trustworthy tier signal.
  return user.adminUser === true ? "admin" : "viewer";
}

/**
 * The effective tier of the currently signed-in user, or `null` if no user is
 * loaded. Read this (never a raw role string) anywhere the UI gates on tier.
 */
export function getUserTier(rootStore: RootStore): UserTier | null {
  return resolveTier(readCurrentUser(rootStore));
}

/** viewer / operator / admin — any authenticated user (a tier is resolvable). */
export function isViewer(rootStore: RootStore): boolean {
  return getUserTier(rootStore) !== null;
}

/**
 * operator OR admin — may start/stop/restart/deploy/rollback and run devops
 * write actions (queue/cancel build, update work item). The single gate the
 * infrastructure stores and devops stores share so the two areas behave
 * identically.
 */
export function isOperator(rootStore: RootStore): boolean {
  const tier = getUserTier(rootStore);
  return tier === "operator" || tier === "admin";
}

/**
 * admin ONLY — may open an exec session (a shell inside a client's container —
 * the highest-privilege action) and configure hosts / connections / secrets.
 */
export function isAdmin(rootStore: RootStore): boolean {
  return getUserTier(rootStore) === "admin";
}
